import { useContext } from 'react'
import { useNavigate } from 'react-router-dom'

import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'

import TaskTimerDisplay from './TaskTimerDisplay'
import SessionTimerDisplay from './SessionTimerDisplay'
import { AuthContext } from '../../../context/AuthContext'
import hokieLogo from '../../../icon/hokie-logo.png'

const CHI_STUDY_SESSIONS = [663, 664, 665, 666]
const TASK_DURATION_SECONDS = 600

export default function Navbar({ sessionId, session, lockedToExercise = false }) {
  const navigate = useNavigate()
  // This Navbar - not HeaderBanner.jsx's, which has its own Profile/My Exercises/Sign Out
  // menu - is what a real student account actually sees while working: it's what
  // WorkspacePage.jsx renders, and where the student links (Distribute, the Session Control
  // lobby link) point for Python and web sessions (studentSurface.js studentPathFor).
  // It had no navigation at all beyond the logo (which goes to '/', not a student's own
  // page) - a real student mid-session had no way back to their past exercises without
  // knowing the /my-exercises URL by heart. Guests (role 3) have no account to return to,
  // so this stays role 4 only.
  const { currentUser } = useContext(AuthContext)
  // lockedToExercise (the workspace, its waiting room and the experiment entry screen): no way
  // off the exercise for a student - no My Exercises, and the wordmark does not navigate - as
  // HeaderBanner already does for Python-session students. In class, students who left that
  // way landed on a session list that does not show their exercise. Staff keep both.
  const locked = lockedToExercise && (currentUser?.role === 3 || currentUser?.role === 4)

  const handleBackToDashboard = () => {
    navigate('/')
  }

  const showTimer = CHI_STUDY_SESSIONS.includes(sessionId)

  return (
    <AppBar position="static" color="primary" elevation={0}>
      <Toolbar 
        disableGutters 
        sx={{ 
          minHeight: '64px',
          paddingLeft: '24px',
          paddingRight: '16px',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="h6"
          noWrap
          component="div"
          onClick={locked ? undefined : handleBackToDashboard}
          sx={{
            fontFamily: 'monospace',
            fontWeight: 700,
            letterSpacing: '.1rem',
            color: 'inherit',
            textDecoration: 'none',
            cursor: locked ? 'default' : 'pointer',
            userSelect: 'none'
          }}
        >
          VizPI
        </Typography>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {showTimer && (
            <TaskTimerDisplay
              duration={TASK_DURATION_SECONDS}
              sessionId={sessionId}
            />
          )}

          <SessionTimerDisplay session={session} />

          {currentUser?.role === 4 && !locked && (
            <Button
              // Was '/my-exercises' (StudentDashboardPage -> read-only StudentRevisitPage).
              // For now, ahead of the in-class rollout, send students back to the same
              // session list '/session' already shows a non-instructor (their own
              // available/joined sessions, role-gated in SessionTable.jsx) rather than into
              // the newer read-only revisit flow.
              onClick={() => navigate('/session')}
              sx={{ color: 'white', textTransform: 'none' }}
            >
              My Exercises
            </Button>
          )}

          {/* Virginia Tech branding, per instructor request - ties the workspace visually
              to the course/university for students, purely decorative (no click behavior). */}
          <img
            src={hokieLogo}
            alt="Virginia Tech"
            style={{ height: '40px', width: 'auto', display: 'block' }}
          />
        </div>
      </Toolbar>
    </AppBar>
  )
}